import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { MessageCircle, ArrowDownLeft, ArrowUpRight, Bot, Loader2, RefreshCw } from "lucide-react";

const INTENT_META = {
  confirm:    { label: "Konfirmoi",      cls: "bg-[#2C4C3B]/15 text-[#2C4C3B] border-[#2C4C3B]/30" },
  cancel:     { label: "Anullim",        cls: "bg-[#9E3C3C]/15 text-[#9E3C3C] border-[#9E3C3C]/30" },
  reschedule: { label: "Spostim",        cls: "bg-[#D4A24C]/15 text-[#8B6914] border-[#D4A24C]/40" },
  other:      { label: "Bisedë e lirë",  cls: "bg-[#EAE4D9] text-[#5C5C5C] border-[#C9C2B2]" },
};

const ACTION_LABELS = {
  konfirmim:       "u konfirmua",
  anulim:          "u anullua",
  spostim:         "u spostua",
  pyet_per_date:   "u kërkua data",
  date_e_paqarte:  "data e paqartë",
  orari_i_zene:    "orari i zënë",
  asnje_takim:     "s'kishte takim",
  pergjigje_lire:  "përgjigje e lirë",
};

const fmtDT = (iso) => iso
  ? new Date(iso).toLocaleString("sq-AL", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false })
  : "—";

export default function PatientWhatsAppChat({ patientId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/patients/${patientId}/whatsapp-messages`);
      setItems(data);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="surface-card p-10 text-center text-sm text-[#5C5C5C]">
        <Loader2 className="inline animate-spin mr-2" size={16} /> Po ngarkohen bisedat…
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="surface-card p-10 text-center text-sm text-[#5C5C5C]" data-testid="wa-empty-state">
        <MessageCircle size={32} className="mx-auto mb-3 text-[#E2DFD8]" />
        Ende s'ka biseda WhatsApp me këtë pacient.
        <p className="text-xs mt-2 text-[#8B7F6D]">
          Kur pacienti të shkruajë në numrin e klinikës (ose asistenti AI t'i përgjigjet),
          çdo mesazh do të shfaqet këtu automatikisht.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="wa-chat-container">
      <div className="flex items-center justify-between">
        <div className="text-xs text-[#5C5C5C]">
          <b>{items.length}</b> mesazhe · Inbound: <b>{items.filter(i => i.direction === "in").length}</b> · Outbound: <b>{items.filter(i => i.direction === "out").length}</b>
        </div>
        <button onClick={load}
                className="inline-flex items-center gap-1 text-xs text-[#2C4C3B] hover:underline"
                data-testid="wa-refresh">
          <RefreshCw size={12} /> Rifresko
        </button>
      </div>

      <div className="surface-card p-4 space-y-3 max-h-[600px] overflow-y-auto bg-[#FDFBF7]">
        {items.map((m) => {
          const isIn = m.direction === "in";
          const intent = m.ai_intent ? INTENT_META[m.ai_intent] : null;
          const actionText = m.action_taken ? ACTION_LABELS[m.action_taken] || m.action_taken : null;
          return (
            <div key={m.id}
                 data-testid={`wa-msg-${m.id}`}
                 className={`flex ${isIn ? "justify-start" : "justify-end"}`}>
              <div className={`max-w-[75%] rounded-lg p-3 border ${
                isIn
                  ? "bg-white border-[#E2DFD8]"
                  : "bg-[#DCF8C6] border-[#B5E39F]"
              }`}>
                <div className="flex items-center gap-2 mb-1 text-[10px] text-[#5C5C5C]">
                  {isIn ? (
                    <><ArrowDownLeft size={10} /> Pacienti</>
                  ) : (
                    <><Bot size={10} className="text-[#2C4C3B]" /> Asistenti AI</>
                  )}
                  <span>·</span>
                  <span>{fmtDT(m.created_at)}</span>
                  {intent && (
                    <span className={`rounded-full px-1.5 py-0.5 border text-[9px] ${intent.cls}`}>
                      {intent.label}
                    </span>
                  )}
                </div>
                <div className="text-sm text-[#1A1A1A] whitespace-pre-wrap break-words">
                  {m.body}
                </div>
                {actionText && !isIn && (
                  <div className="mt-1 text-[10px] text-[#5C5C5C] italic">
                    → Veprimi: {actionText}
                  </div>
                )}
                {!isIn && m.direction === "out" && (
                  <div className="flex items-center gap-1 mt-1 text-[10px] text-[#25D366]">
                    <ArrowUpRight size={10} /> Dërguar
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="text-[11px] text-[#8B7F6D] bg-[#D4A24C]/10 border border-[#D4A24C]/30 rounded p-2.5">
        💡 <b>Këshillë:</b> Mesazhet ruhen automatikisht kur pacienti shkruan në numrin e klinikës (webhook Twilio) dhe kur asistenti AI i përgjigjet. Për të nisur një bisedë të re me këtë pacient, përdorni butonin WhatsApp në krye të faqes.
      </div>
    </div>
  );
}
